import React, { useState, useEffect, useRef } from 'react';

export default function ThoughtChain({ steps, isStreaming, isToolCallStep, isThinkingStep }) {
  const [isOpen, setIsOpen] = useState(isStreaming);
  const [wasFinished, setWasFinished] = useState(!isStreaming);
  const prevStreaming = useRef(isStreaming);

  useEffect(() => {
    // If it transitions from streaming to not streaming, auto-collapse
    if (prevStreaming.current && !isStreaming) {
      setIsOpen(false);
      setWasFinished(true); // Ensure it remembers it's finished
    } else if (!prevStreaming.current && isStreaming) {
      // If it starts streaming again for some reason, expand it
      setIsOpen(true);
      setWasFinished(false);
    }
    prevStreaming.current = isStreaming;
  }, [isStreaming]);

  if (!steps || steps.length === 0) return null;

  return (
    <details 
      className={`mb-3 group`} 
      open={isOpen}
      onToggle={(e) => setIsOpen(e.target.open)}
    >
      <summary className="cursor-pointer text-xs select-none flex items-center gap-1.5 py-1" style={{color: 'rgba(255,255,255,0.5)'}}>
        {(isStreaming || steps.some(s => s.status === 'running')) ? (
          <svg className="w-3.5 h-3.5 animate-spin" style={{color: 'var(--tqa-green)'}} fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
        <span>
          {isStreaming
            ? `Working (${steps.length} step${steps.length !== 1 ? 's' : ''})`
            : `Thought for ${steps.length} step${steps.length !== 1 ? 's' : ''}`
          }
        </span>
        <svg className="w-3 h-3 transition-transform group-open:rotate-90" style={{color: 'rgba(255,255,255,0.4)'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </summary>
      <div className="mt-1.5 ml-1 pl-3 space-y-1" style={{borderLeft: '2px solid var(--tqa-border-dark)'}}>
        {steps.map((step, idx) => (
          <div key={idx} className={`flex items-center gap-2 text-xs py-0.5 w-full min-w-0 ${
            isToolCallStep(step) ? 'ml-3' : ''
          }`} style={{color: 'rgba(255,255,255,0.5)'}}>
            {isThinkingStep(step) ? (
              <svg
                className={`w-3.5 h-3.5 ${step.status === 'done' ? 'text-green-500' : 'text-amber-400'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9.75 17h4.5M10 20h4M12 3.5a6.5 6.5 0 00-4.4 11.28c.62.57 1.15 1.23 1.4 1.97h5.99c.25-.74.78-1.4 1.4-1.97A6.5 6.5 0 0012 3.5z" />
              </svg>
            ) : (
              <svg
                className={`w-4 h-4 ${step.status === 'running' ? 'text-blue-500 animate-spin' : step.status === 'failed' ? 'text-red-500' : 'text-green-500'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10.325 4.317a1 1 0 011.35-.936l.138.055 1.2.6a1 1 0 00.894 0l1.2-.6a1 1 0 011.488.75l.018.186v1.35a1 1 0 00.447.832l1.134.756a1 1 0 01.181 1.538l-.124.141-.954.955a1 1 0 00-.264.908l.3 1.424a1 1 0 01-1.09 1.19l-.183-.022-1.423-.3a1 1 0 00-.908.264l-.955.954a1 1 0 01-1.538-.18l-.141-.182-.756-1.134a1 1 0 00-.832-.447h-1.35a1 1 0 01-.936-1.35l.055-.138.6-1.2a1 1 0 000-.894l-.6-1.2a1 1 0 01.75-1.488l.186-.018h1.35a1 1 0 00.832-.447l.756-1.134z" />
                <circle cx="12" cy="12" r="2.2" strokeWidth={1.8} />
              </svg>
            )}
            <span className="flex-shrink-0">{step.label}</span>
            {step.detail && step.detail !== step.label && (
              <span className="flex-1 min-w-0 truncate" style={{color: 'rgba(255,255,255,0.35)'}} title={step.fullDetail || step.detail}>{step.detail}</span>
            )}
            {step.status === 'done' && step.durationMs && (
              <span className="flex-shrink-0" style={{color: 'rgba(255,255,255,0.35)'}}>{(step.durationMs / 1000).toFixed(1)}s</span>
            )}
          </div>
        ))}
      </div>
    </details>
  );
}
